import React, { useState, useEffect, useMemo } from "react";
import DashboardWrapper from "../components/layout/DashboardWrapper";
import { AlertRuleService } from "../services/alertRuleService";
import { AlertRule, AlertTier } from "../types/alertRule";
import { PlusIcon, PencilIcon, TrashIcon, XMarkIcon } from "@heroicons/react/24/outline";
import {
  Clause,
  RangeOperator,
  RatingOperator,
  CYCLE_PHASE_OPTIONS,
  createDefaultClause,
  isClauseComplete,
  serializeClauses,
  parseCondition,
} from "../utils/alertConditionBuilder";

const TIER_ORDER: AlertTier[] = ["Red", "Amber", "Green"];

const TIER_BADGE_CLASSES: Record<AlertTier, string> = {
  Red: "bg-red-100 text-red-700 border-red-200",
  Amber: "bg-amber-100 text-amber-700 border-amber-200",
  Green: "bg-green-100 text-green-700 border-green-200",
};

// Baseline drivers seen across the migrated rule set - the actual dropdown
// also folds in whatever is currently used in Firestore (see driverOptions).
const KNOWN_TRIGGER_DRIVERS = [
  "Cycle Phase",
  "HLR",
  "Health",
  "Mood",
  "Movement Quality",
  "Peak Score",
  "Recovery",
  "Session Load",
  "Sleep",
  "Soreness",
  "Stress",
  "Training Gap",
  "Wellness",
  "Workload Factor",
];

type ClauseVariableKey =
  | "WLF"
  | "HLR"
  | "Wellness"
  | "cyclePhase"
  | "sleepRating"
  | "stressRating"
  | "injured"
  | "canTrain"
  | "sickFactor";

const VARIABLE_OPTIONS: { value: ClauseVariableKey; label: string }[] = [
  { value: "WLF", label: "Workload Factor (WLF)" },
  { value: "HLR", label: "HLR" },
  { value: "Wellness", label: "Wellness" },
  { value: "cyclePhase", label: "Cycle Phase" },
  { value: "sleepRating", label: "Sleep Rating" },
  { value: "stressRating", label: "Stress Rating" },
  { value: "injured", label: "Injured" },
  { value: "canTrain", label: "Can Train" },
  { value: "sickFactor", label: "Sick Factor" },
];

const RANGE_OPERATOR_OPTIONS: { value: RangeOperator; label: string }[] = [
  { value: "between", label: "Between" },
  { value: ">", label: "Greater than (>)" },
  { value: "<", label: "Less than (<)" },
  { value: "≥", label: "At least (≥)" },
  { value: "≤", label: "At most (≤)" },
];

const RATING_OPERATOR_OPTIONS: { value: RatingOperator; label: string }[] = [
  { value: "=", label: "Exactly (=)" },
  { value: "≥", label: "At least (≥)" },
  { value: "≤", label: "At most (≤)" },
  { value: ">", label: "Greater than (>)" },
  { value: "<", label: "Less than (<)" },
];

const getClauseVariableKey = (clause: Clause): ClauseVariableKey => {
  if (clause.type === "cyclePhase") return "cyclePhase";
  return clause.variable;
};

const createClauseForVariable = (key: ClauseVariableKey): Clause => {
  switch (key) {
    case "WLF":
    case "HLR":
    case "Wellness":
      return { type: "range", variable: key, operator: "between", min: "", max: "", value: "" };
    case "cyclePhase":
      return { type: "cyclePhase", mode: "equals", phases: [] };
    case "sleepRating":
    case "stressRating":
      return { type: "rating", variable: key, operator: "≥", value: "" };
    case "injured":
    case "canTrain":
    case "sickFactor":
      return { type: "boolean", variable: key, value: true };
    default:
      return createDefaultClause();
  }
};

interface AlertRuleFormState {
  alertId: string;
  alertName: string;
  alertTier: AlertTier;
  conditionMode: "builder" | "freeform";
  clauses: Clause[];
  freeformCondition: string;
  primaryTriggerDriver: string;
  secondaryTriggerDriver: string;
  athleteInsight: string;
}

const EMPTY_FORM: AlertRuleFormState = {
  alertId: "",
  alertName: "",
  alertTier: "Amber",
  conditionMode: "builder",
  clauses: [createDefaultClause()],
  freeformCondition: "",
  primaryTriggerDriver: "",
  secondaryTriggerDriver: "",
  athleteInsight: "",
};

// PA1-PA15 are evaluated by hardcoded multi-day logic in alertService.ts -
// their "condition" text is documentation only, never parsed by the engine.
const isMultiDayPersonalisedAlert = (alertId: string): boolean => {
  const match = alertId.match(/^PA(\d+)$/);
  if (!match) return false;
  const num = parseInt(match[1], 10);
  return num >= 1 && num <= 15;
};

const AlertRulesPage: React.FC = () => {
  const [rules, setRules] = useState<AlertRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [search, setSearch] = useState("");
  const [tierFilter, setTierFilter] = useState<AlertTier | "all">("all");

  const [isAdding, setIsAdding] = useState(false);
  const [editingRuleId, setEditingRuleId] = useState<string | null>(null);
  const [form, setForm] = useState<AlertRuleFormState>(EMPTY_FORM);

  useEffect(() => {
    fetchRules();
  }, []);

  const fetchRules = async () => {
    try {
      setLoading(true);
      setError(null);
      const fetched = await AlertRuleService.fetchAlertRules();
      setRules(fetched);
    } catch (err) {
      console.error("Error fetching alert rules:", err);
      setError(err instanceof Error ? err.message : "Failed to fetch alert rules");
    } finally {
      setLoading(false);
    }
  };

  const closeForm = () => {
    setIsAdding(false);
    setEditingRuleId(null);
    setForm(EMPTY_FORM);
  };

  const startEditing = (rule: AlertRule) => {
    setIsAdding(false);
    setEditingRuleId(rule.id || rule.alertId);
    const parsedClauses = parseCondition(rule.condition);
    setForm({
      alertId: rule.alertId,
      alertName: rule.alertName,
      alertTier: rule.alertTier,
      conditionMode: parsedClauses ? "builder" : "freeform",
      clauses: parsedClauses || [createDefaultClause()],
      freeformCondition: parsedClauses ? "" : rule.condition,
      primaryTriggerDriver: rule.primaryTriggerDriver || "",
      secondaryTriggerDriver: rule.secondaryTriggerDriver || "",
      athleteInsight: rule.athleteInsight || "",
    });
  };

  const finalCondition =
    form.conditionMode === "builder" ? serializeClauses(form.clauses) : form.freeformCondition;

  const isConditionValid =
    form.conditionMode === "builder"
      ? form.clauses.length > 0 && form.clauses.every(isClauseComplete)
      : form.freeformCondition.trim() !== "";

  const switchToFreeform = () => {
    setForm((prev) => ({
      ...prev,
      conditionMode: "freeform",
      freeformCondition: serializeClauses(prev.clauses),
    }));
  };

  const switchToBuilder = () => {
    const parsed = parseCondition(form.freeformCondition);
    if (!parsed) {
      window.alert(
        "This text doesn't match the structured builder format (this is expected for personalised multi-day alerts like PA1-PA15). Keeping it as freeform text."
      );
      return;
    }
    setForm((prev) => ({ ...prev, conditionMode: "builder", clauses: parsed }));
  };

  const addClause = () => {
    setForm((prev) => ({ ...prev, clauses: [...prev.clauses, createDefaultClause()] }));
  };

  const removeClause = (index: number) => {
    setForm((prev) => ({ ...prev, clauses: prev.clauses.filter((_, i) => i !== index) }));
  };

  const updateClause = (index: number, updater: (clause: Clause) => Clause) => {
    setForm((prev) => ({
      ...prev,
      clauses: prev.clauses.map((clause, i) => (i === index ? updater(clause) : clause)),
    }));
  };

  const handleSave = async () => {
    if (!form.alertId.trim() || !form.alertName.trim() || !isConditionValid) {
      setError("Alert ID, name, and a complete condition are required");
      return;
    }

    try {
      if (editingRuleId) {
        await AlertRuleService.updateAlertRule(editingRuleId, {
          alertName: form.alertName,
          alertTier: form.alertTier,
          condition: finalCondition,
          primaryTriggerDriver: form.primaryTriggerDriver,
          secondaryTriggerDriver: form.secondaryTriggerDriver,
          athleteInsight: form.athleteInsight,
        });
      } else {
        await AlertRuleService.createAlertRule({
          alertId: form.alertId,
          alertName: form.alertName,
          alertTier: form.alertTier,
          condition: finalCondition,
          primaryTriggerDriver: form.primaryTriggerDriver,
          secondaryTriggerDriver: form.secondaryTriggerDriver,
          athleteInsight: form.athleteInsight,
        });
      }
      closeForm();
      fetchRules();
    } catch (err) {
      console.error("Error saving alert rule:", err);
      setError(err instanceof Error ? err.message : "Failed to save alert rule");
    }
  };

  const handleDelete = async (ruleId: string, alertName: string) => {
    if (!window.confirm(`Delete alert rule "${alertName}"? This cannot be undone.`)) {
      return;
    }

    try {
      await AlertRuleService.deleteAlertRule(ruleId);
      fetchRules();
    } catch (err) {
      console.error("Error deleting alert rule:", err);
      setError(err instanceof Error ? err.message : "Failed to delete alert rule");
    }
  };

  const driverOptions = useMemo(() => {
    const used = rules.flatMap((rule) => [rule.primaryTriggerDriver, rule.secondaryTriggerDriver]);
    return Array.from(new Set([...KNOWN_TRIGGER_DRIVERS, ...used])).filter(Boolean).sort();
  }, [rules]);

  const filteredRules = useMemo(() => {
    const term = search.trim().toLowerCase();
    return rules.filter((rule) => {
      if (tierFilter !== "all" && rule.alertTier !== tierFilter) return false;
      if (!term) return true;
      return (
        rule.alertId.toLowerCase().includes(term) ||
        rule.alertName.toLowerCase().includes(term) ||
        rule.condition.toLowerCase().includes(term)
      );
    });
  }, [rules, search, tierFilter]);

  const rulesByTier = useMemo(() => {
    const grouped: Record<AlertTier, AlertRule[]> = { Red: [], Amber: [], Green: [] };
    for (const rule of filteredRules) {
      if (grouped[rule.alertTier]) {
        grouped[rule.alertTier].push(rule);
      }
    }
    for (const tier of TIER_ORDER) {
      grouped[tier].sort((a, b) => a.alertId.localeCompare(b.alertId, undefined, { numeric: true }));
    }
    return grouped;
  }, [filteredRules]);

  const isFormOpen = isAdding || !!editingRuleId;
  const showPersonalisedHint = isMultiDayPersonalisedAlert(form.alertId.trim());

  return (
    <DashboardWrapper>
      <div className="w-full flex flex-col gap-6 p-6 h-[calc(100vh-64px)] overflow-hidden">
        {/* Add / Edit Modal */}
        {isFormOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
              <div className="flex items-center justify-between p-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold">
                  {editingRuleId ? "Edit Alert Rule" : "Add Alert Rule"}
                </h3>
                <button onClick={closeForm} className="p-1 hover:bg-gray-100 rounded">
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">
                      Alert ID *
                    </label>
                    <input
                      type="text"
                      placeholder="e.g., G1, R5, PA3"
                      disabled={!!editingRuleId}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary/50 disabled:bg-gray-100 disabled:text-gray-500"
                      value={form.alertId}
                      onChange={(e) => setForm((prev) => ({ ...prev, alertId: e.target.value }))}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">
                      Tier *
                    </label>
                    <select
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary/50"
                      value={form.alertTier}
                      onChange={(e) =>
                        setForm((prev) => ({ ...prev, alertTier: e.target.value as AlertTier }))
                      }
                    >
                      {TIER_ORDER.map((tier) => (
                        <option key={tier} value={tier}>
                          {tier}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">
                    Alert Name *
                  </label>
                  <input
                    type="text"
                    placeholder="e.g., Recovering Well"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary/50"
                    value={form.alertName}
                    onChange={(e) => setForm((prev) => ({ ...prev, alertName: e.target.value }))}
                  />
                </div>

                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="block text-xs font-medium text-gray-500">Condition *</label>
                    <div className="flex items-center gap-1 text-xs">
                      <button
                        type="button"
                        onClick={() => form.conditionMode !== "builder" && switchToBuilder()}
                        className={`px-2 py-1 rounded ${
                          form.conditionMode === "builder"
                            ? "bg-gray-900 text-white"
                            : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                        }`}
                      >
                        Builder
                      </button>
                      <button
                        type="button"
                        onClick={() => form.conditionMode !== "freeform" && switchToFreeform()}
                        className={`px-2 py-1 rounded ${
                          form.conditionMode === "freeform"
                            ? "bg-gray-900 text-white"
                            : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                        }`}
                      >
                        Advanced (text)
                      </button>
                    </div>
                  </div>

                  {showPersonalisedHint && (
                    <p className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded px-2 py-1 mb-2">
                      PA1-PA15 are multi-day personalised alerts. Their trigger logic is hardcoded
                      in PeakElite - this condition text is a description only and isn't evaluated,
                      so freeform text is expected here.
                    </p>
                  )}

                  {form.conditionMode === "builder" ? (
                    <div className="space-y-2">
                      {form.clauses.map((clause, index) => (
                        <div key={index} className="space-y-1">
                          {index > 0 && (
                            <div className="text-[11px] font-semibold text-gray-400 pl-1">AND</div>
                          )}
                          <div className="flex items-start gap-2 p-2 border border-gray-200 rounded-lg bg-gray-50">
                            <select
                              className="px-2 py-1.5 border border-gray-300 rounded text-xs bg-white focus:outline-none focus:ring-2 focus:ring-primary/50"
                              value={getClauseVariableKey(clause)}
                              onChange={(e) =>
                                updateClause(index, () =>
                                  createClauseForVariable(e.target.value as ClauseVariableKey)
                                )
                              }
                            >
                              {VARIABLE_OPTIONS.map((opt) => (
                                <option key={opt.value} value={opt.value}>
                                  {opt.label}
                                </option>
                              ))}
                            </select>

                            <div className="flex-1 flex flex-wrap items-center gap-2">
                              {clause.type === "range" && (
                                <>
                                  <select
                                    className="px-2 py-1.5 border border-gray-300 rounded text-xs bg-white focus:outline-none focus:ring-2 focus:ring-primary/50"
                                    value={clause.operator}
                                    onChange={(e) =>
                                      updateClause(index, (c) =>
                                        c.type === "range"
                                          ? { ...c, operator: e.target.value as RangeOperator }
                                          : c
                                      )
                                    }
                                  >
                                    {RANGE_OPERATOR_OPTIONS.map((opt) => (
                                      <option key={opt.value} value={opt.value}>
                                        {opt.label}
                                      </option>
                                    ))}
                                  </select>
                                  {clause.operator === "between" ? (
                                    <>
                                      <input
                                        type="number"
                                        step="0.01"
                                        placeholder="Min"
                                        className="w-24 px-2 py-1.5 border border-gray-300 rounded text-xs bg-white focus:outline-none focus:ring-2 focus:ring-primary/50"
                                        value={clause.min}
                                        onChange={(e) =>
                                          updateClause(index, (c) =>
                                            c.type === "range" ? { ...c, min: e.target.value } : c
                                          )
                                        }
                                      />
                                      <span className="text-xs text-gray-400">and</span>
                                      <input
                                        type="number"
                                        step="0.01"
                                        placeholder="Max"
                                        className="w-24 px-2 py-1.5 border border-gray-300 rounded text-xs bg-white focus:outline-none focus:ring-2 focus:ring-primary/50"
                                        value={clause.max}
                                        onChange={(e) =>
                                          updateClause(index, (c) =>
                                            c.type === "range" ? { ...c, max: e.target.value } : c
                                          )
                                        }
                                      />
                                    </>
                                  ) : (
                                    <input
                                      type="number"
                                      step="0.01"
                                      placeholder="Value"
                                      className="w-24 px-2 py-1.5 border border-gray-300 rounded text-xs bg-white focus:outline-none focus:ring-2 focus:ring-primary/50"
                                      value={clause.value}
                                      onChange={(e) =>
                                        updateClause(index, (c) =>
                                          c.type === "range" ? { ...c, value: e.target.value } : c
                                        )
                                      }
                                    />
                                  )}
                                </>
                              )}

                              {clause.type === "cyclePhase" && (
                                <>
                                  <select
                                    className="px-2 py-1.5 border border-gray-300 rounded text-xs bg-white focus:outline-none focus:ring-2 focus:ring-primary/50"
                                    value={clause.mode}
                                    onChange={(e) =>
                                      updateClause(index, (c) =>
                                        c.type === "cyclePhase"
                                          ? { ...c, mode: e.target.value as "equals" | "in" }
                                          : c
                                      )
                                    }
                                  >
                                    <option value="equals">Is exactly</option>
                                    <option value="in">Is one of</option>
                                  </select>
                                  {clause.mode === "equals" ? (
                                    <select
                                      className="px-2 py-1.5 border border-gray-300 rounded text-xs bg-white focus:outline-none focus:ring-2 focus:ring-primary/50"
                                      value={clause.phases[0] || ""}
                                      onChange={(e) =>
                                        updateClause(index, (c) =>
                                          c.type === "cyclePhase"
                                            ? { ...c, phases: [e.target.value] }
                                            : c
                                        )
                                      }
                                    >
                                      <option value="" disabled>
                                        Select phase...
                                      </option>
                                      {CYCLE_PHASE_OPTIONS.map((phase) => (
                                        <option key={phase} value={phase}>
                                          {phase}
                                        </option>
                                      ))}
                                    </select>
                                  ) : (
                                    <div className="flex flex-wrap gap-2">
                                      {CYCLE_PHASE_OPTIONS.map((phase) => {
                                        const checked = clause.phases.includes(phase);
                                        return (
                                          <label
                                            key={phase}
                                            className="flex items-center gap-1 text-xs bg-white border border-gray-300 rounded px-2 py-1 cursor-pointer"
                                          >
                                            <input
                                              type="checkbox"
                                              checked={checked}
                                              onChange={() =>
                                                updateClause(index, (c) =>
                                                  c.type === "cyclePhase"
                                                    ? {
                                                        ...c,
                                                        phases: checked
                                                          ? c.phases.filter((p) => p !== phase)
                                                          : [...c.phases, phase],
                                                      }
                                                    : c
                                                )
                                              }
                                              className="w-3 h-3"
                                            />
                                            {phase}
                                          </label>
                                        );
                                      })}
                                    </div>
                                  )}
                                </>
                              )}

                              {clause.type === "rating" && (
                                <>
                                  <select
                                    className="px-2 py-1.5 border border-gray-300 rounded text-xs bg-white focus:outline-none focus:ring-2 focus:ring-primary/50"
                                    value={clause.operator}
                                    onChange={(e) =>
                                      updateClause(index, (c) =>
                                        c.type === "rating"
                                          ? { ...c, operator: e.target.value as RatingOperator }
                                          : c
                                      )
                                    }
                                  >
                                    {RATING_OPERATOR_OPTIONS.map((opt) => (
                                      <option key={opt.value} value={opt.value}>
                                        {opt.label}
                                      </option>
                                    ))}
                                  </select>
                                  <input
                                    type="number"
                                    min={1}
                                    max={7}
                                    placeholder="1-7"
                                    className="w-16 px-2 py-1.5 border border-gray-300 rounded text-xs bg-white focus:outline-none focus:ring-2 focus:ring-primary/50"
                                    value={clause.value}
                                    onChange={(e) =>
                                      updateClause(index, (c) =>
                                        c.type === "rating" ? { ...c, value: e.target.value } : c
                                      )
                                    }
                                  />
                                  <span className="text-xs text-gray-400">/ 7</span>
                                </>
                              )}

                              {clause.type === "boolean" && (
                                <select
                                  className="px-2 py-1.5 border border-gray-300 rounded text-xs bg-white focus:outline-none focus:ring-2 focus:ring-primary/50"
                                  value={clause.value ? "true" : "false"}
                                  onChange={(e) =>
                                    updateClause(index, (c) =>
                                      c.type === "boolean"
                                        ? { ...c, value: e.target.value === "true" }
                                        : c
                                    )
                                  }
                                >
                                  <option value="true">True</option>
                                  <option value="false">False</option>
                                </select>
                              )}
                            </div>

                            <button
                              type="button"
                              onClick={() => removeClause(index)}
                              disabled={form.clauses.length === 1}
                              className="p-1.5 hover:bg-red-100 rounded disabled:opacity-30 disabled:cursor-not-allowed"
                              title="Remove condition"
                            >
                              <TrashIcon className="w-3.5 h-3.5 text-red-500" />
                            </button>
                          </div>
                        </div>
                      ))}

                      <button
                        type="button"
                        onClick={addClause}
                        className="flex items-center gap-1 text-xs text-gray-600 hover:text-gray-900 px-2 py-1"
                      >
                        <PlusIcon className="w-3.5 h-3.5" />
                        Add condition (AND)
                      </button>

                      <p className="text-xs text-gray-400 font-mono bg-gray-50 border border-gray-200 rounded px-2 py-1 break-words">
                        {finalCondition || "(no conditions yet)"}
                      </p>
                    </div>
                  ) : (
                    <textarea
                      placeholder="e.g., WLF 1.10–1.25 AND HLR 1.00–1.03 AND Wellness 75–90"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary/50 resize-none font-mono"
                      rows={2}
                      value={form.freeformCondition}
                      onChange={(e) =>
                        setForm((prev) => ({ ...prev, freeformCondition: e.target.value }))
                      }
                    />
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">
                      Primary Trigger Driver
                    </label>
                    <select
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary/50"
                      value={form.primaryTriggerDriver}
                      onChange={(e) =>
                        setForm((prev) => ({ ...prev, primaryTriggerDriver: e.target.value }))
                      }
                    >
                      <option value="">(none)</option>
                      {driverOptions.map((driver) => (
                        <option key={driver} value={driver}>
                          {driver}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">
                      Secondary Trigger Driver
                    </label>
                    <select
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary/50"
                      value={form.secondaryTriggerDriver}
                      onChange={(e) =>
                        setForm((prev) => ({ ...prev, secondaryTriggerDriver: e.target.value }))
                      }
                    >
                      <option value="">(none)</option>
                      {driverOptions.map((driver) => (
                        <option key={driver} value={driver}>
                          {driver}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">
                    Athlete Insight
                  </label>
                  <textarea
                    placeholder="Message shown to the athlete/coach when this alert triggers..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary/50 resize-none"
                    rows={4}
                    value={form.athleteInsight}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, athleteInsight: e.target.value }))
                    }
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3 p-4 border-t border-gray-200">
                <button
                  onClick={closeForm}
                  className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={!form.alertId.trim() || !form.alertName.trim() || !isConditionValid}
                  className="px-4 py-2 text-sm bg-gray-900 text-white rounded-lg hover:bg-black transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {editingRuleId ? "Save Changes" : "Add Rule"}
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-heading">Alert Rules</h1>
            <p className="text-sm text-gray-500 mt-1">
              {rules.length} rule{rules.length === 1 ? "" : "s"} · read live by PeakElite, no
              redeploy needed
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                setIsAdding(true);
                setEditingRuleId(null);
                setForm(EMPTY_FORM);
              }}
              className="btn btn-primary btn-sm !h-10 gap-2"
            >
              <PlusIcon className="w-4 h-4" />
              Add Rule
            </button>
          </div>
        </div>

        {error && (
          <div className="p-3 text-sm bg-red-50 text-red-500 border border-red-200 rounded-lg flex justify-between items-center">
            <span>{error}</span>
            <button onClick={() => setError(null)} className="p-1 hover:bg-red-100 rounded">
              <XMarkIcon className="w-4 h-4" />
            </button>
          </div>
        )}

        <div className="flex items-center gap-3">
          <input
            type="text"
            placeholder="Search by ID, name, or condition..."
            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary/50"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <select
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary/50"
            value={tierFilter}
            onChange={(e) => setTierFilter(e.target.value as AlertTier | "all")}
          >
            <option value="all">All Tiers</option>
            {TIER_ORDER.map((tier) => (
              <option key={tier} value={tier}>
                {tier}
              </option>
            ))}
          </select>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto space-y-6">
          {loading ? (
            <div className="flex items-center justify-center h-32 text-gray-400">
              Loading alert rules...
            </div>
          ) : filteredRules.length === 0 ? (
            <div className="flex items-center justify-center h-32 text-gray-400">
              {rules.length === 0
                ? 'No alert rules yet. Click "Add Rule" to get started.'
                : "No alert rules match your search."}
            </div>
          ) : (
            TIER_ORDER.filter((tier) => rulesByTier[tier].length > 0).map((tier) => (
              <div key={tier} className="bg-white rounded-xl border overflow-hidden">
                <div className="px-4 py-3 bg-gray-50 border-b flex items-center gap-2">
                  <span
                    className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${TIER_BADGE_CLASSES[tier]}`}
                  >
                    {tier}
                  </span>
                  <span className="text-sm text-gray-500">
                    {rulesByTier[tier].length} rule{rulesByTier[tier].length === 1 ? "" : "s"}
                  </span>
                </div>
                <div className="divide-y divide-gray-100">
                  {rulesByTier[tier].map((rule) => (
                    <div
                      key={rule.id || rule.alertId}
                      className="px-4 py-3 flex items-start justify-between gap-4 hover:bg-gray-50"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-mono text-gray-400">{rule.alertId}</span>
                          <span className="text-sm font-semibold text-gray-900">
                            {rule.alertName}
                          </span>
                        </div>
                        <p className="text-xs text-gray-500 font-mono mt-1 break-words">
                          {rule.condition}
                        </p>
                        {rule.athleteInsight && (
                          <p className="text-xs text-gray-400 mt-1 line-clamp-2">
                            {rule.athleteInsight}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <button
                          onClick={() => startEditing(rule)}
                          className="p-1.5 hover:bg-gray-200 rounded"
                          title="Edit"
                        >
                          <PencilIcon className="w-4 h-4 text-gray-500" />
                        </button>
                        <button
                          onClick={() => handleDelete(rule.id || rule.alertId, rule.alertName)}
                          className="p-1.5 hover:bg-red-100 rounded"
                          title="Delete"
                        >
                          <TrashIcon className="w-4 h-4 text-red-500" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </DashboardWrapper>
  );
};

export default AlertRulesPage;
